import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { ADMIN_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { toast } from "sonner";
import EmployeeMap from "../../components/EmployeeMap";
import DiskGauge from "../../components/DiskGauge";
import { Link } from "react-router-dom";
import { Users, UserCheck, UserMinus, Tags, Car, Activity, CalendarCheck, Clock4, ChevronRight } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState([]);
  const [disk, setDisk] = useState(null);
  const [scheduleCounts, setScheduleCounts] = useState({ pending: 0, pending_change_requests: 0, approved: 0 });
  const [scheduler, setScheduler] = useState(null);

  async function load() {
    try {
      const [s, l, d, sc, sch] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/live-employees"),
        api.get("/admin/disk-usage"),
        api.get("/admin/schedules/counts"),
        api.get("/admin/scheduler/status"),
      ]);
      setStats(s.data); setLive(l.data); setDisk(d.data);
      setScheduleCounts(sc.data); setScheduler(sch.data);
    } catch (e) { toast.error(errFrom(e)); }
  }
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  return (
    <AppShell nav={ADMIN_NAV} title="Operations overview" subtitle="Control Room">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
        <Stat label="Active" value={stats?.employees?.active ?? 0} icon={<UserCheck size={16} />} />
        <Stat label="Onboarding" value={stats?.employees?.onboarding ?? 0} icon={<Users size={16} />} />
        <Stat label="Live now" value={stats?.live_punched_in ?? 0} icon={<Activity size={16} />} accent />
        <Stat label="Terminated" value={stats?.employees?.terminated ?? 0} icon={<UserMinus size={16} />} />
        <Stat label="Today tags" value={stats?.today_tags ?? 0} icon={<Tags size={16} />} />
        <Stat label="Today cars" value={stats?.today_inspections ?? 0} icon={<Car size={16} />} />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 brand-surface rounded-2xl p-4 md:p-6" data-testid="live-employees-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="font-heading text-lg md:text-xl font-bold">Live employees</h2>
              <p className="text-xs text-zinc-500">Currently punched-in with locations</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> {live.length} online
            </div>
          </div>
          <div className="h-64 md:h-80">
            <EmployeeMap points={live} />
          </div>
          <div className="mt-4 -mx-4 md:mx-0 overflow-x-auto">
            {live.length === 0 ? (
              <div className="text-center text-zinc-500 text-sm py-6 px-4">No one's punched in right now.</div>
            ) : (
              <table className="w-full text-sm min-w-[560px] px-4 md:px-0">
                <thead>
                  <tr className="text-overline text-left">
                    <th className="py-2 px-3">Employee</th>
                    <th className="py-2 px-3">Clocked in</th>
                    <th className="py-2 px-3">Address</th>
                  </tr>
                </thead>
                <tbody>
                  {live.map((r) => (
                    <tr key={r.id} className="border-t border-white/5" data-testid={`live-row-${r.id}`}>
                      <td className="py-3 px-3 font-semibold whitespace-nowrap">{r.user_name}</td>
                      <td className="py-3 px-3 text-zinc-400 whitespace-nowrap">{new Date(r.clock_in_at).toLocaleString()}</td>
                      <td className="py-3 px-3 text-zinc-500 max-w-md truncate">{r.address || `${r.lat?.toFixed?.(4)}, ${r.lng?.toFixed?.(4)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 md:gap-6">
          {disk && (
            <DiskGauge
              percent={disk.percent_used_of_limit}
              usedGb={disk.uploads_gb}
              limitGb={disk.limit_gb}
              fileCount={disk.uploads_file_count}
              serverUsedGb={disk.server_used_gb}
              serverTotalGb={disk.server_total_gb}
            />
          )}
          <div className="brand-surface rounded-2xl p-6">
            <div className="text-overline mb-2">Dealerships</div>
            <div className="font-mono text-4xl font-bold">{stats?.dealerships_count ?? 0}</div>
            <p className="text-xs text-zinc-500 mt-2">Active partner dealerships</p>
          </div>

          <Link to="/admin/schedules" className="brand-surface rounded-2xl p-6 hover-lift block group" data-testid="schedules-widget">
            <div className="flex items-center justify-between">
              <div className="text-overline flex items-center gap-1.5"><CalendarCheck size={12} />Schedules</div>
              <ChevronRight size={14} className="text-zinc-600 group-hover:text-[#FF3B30] transition-colors" />
            </div>
            <div className="flex items-baseline gap-2 mt-3">
              <span className={`font-mono font-bold text-4xl ${scheduleCounts.pending > 0 ? "text-[#FF3B30]" : "text-white"}`}>{scheduleCounts.pending}</span>
              <span className="text-xs text-zinc-500">pending</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
              {scheduleCounts.pending_change_requests > 0 && (
                <span className="bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider">{scheduleCounts.pending_change_requests} change req</span>
              )}
              <span className="bg-green-500/15 text-green-400 border border-green-500/30 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider">{scheduleCounts.approved} approved</span>
            </div>
          </Link>

          <div className="brand-surface rounded-2xl p-6" data-testid="scheduler-widget">
            <div className="text-overline flex items-center gap-1.5 mb-2"><Clock4 size={12} />Auto Weekly Report</div>
            {scheduler?.running ? (
              <>
                <div className="text-xs text-zinc-500 mt-1">Next run</div>
                <div className="font-mono text-lg font-bold text-white mt-1">
                  {scheduler.next_run ? new Date(scheduler.next_run).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                </div>
                <div className="flex items-center gap-2 mt-3 text-[11px]">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-zinc-400">Scheduler active · {scheduler.timezone}</span>
                </div>
              </>
            ) : (
              <div className="text-sm text-red-400 mt-2">Scheduler offline</div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, icon, accent }) {
  return (
    <div className={`brand-surface rounded-xl p-4 hover-lift ${accent ? "border-[#FF3B30]/30" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-zinc-500">
        <span className="text-overline">{label}</span>
        <span className={accent ? "text-[#FF3B30]" : "text-zinc-600"}>{icon}</span>
      </div>
      <div className={`font-mono font-bold text-3xl mt-3 ${accent ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
    </div>
  );
}
